import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { PEOPLE_ORIGIN, marketingHead } from "@/lib/marketing/seo";
import { PUBLISHER, WEBSITE_ID, breadcrumbs } from "@/lib/marketing/ppl-schema";
import { useState, type FormEvent } from "react";
import { MLink } from "@/lib/marketing/m-link";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal } from "@/components/marketing/reveal";
import { orpcPublicClient } from "@/lib/orpc";
import { toast } from "sonner";
import { readAttribution } from "@/lib/marketing/attribution";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { IntakeHero } from "@/components/marketing/site/intake-hero";
import { IntakeNameFields } from "@/components/marketing/site/intake-name-fields";
import { IntakePhoneField } from "@/components/marketing/site/intake-phone-field";
import { IntakeReasons } from "@/components/marketing/site/intake-reasons";
import { Mail, Rocket, Clock, ShieldCheck, ArrowRight, CheckCircle2 } from "@/lib/public-icons";

import { CONTACT_EMAIL } from "@alm/domain/marketing";

/* The endpoint caps the message at 2000 characters and the organization rides inside it, so the two
 * fields are bounded to fit together rather than letting a long note come back as a validation
 * error the visitor cannot see the cause of. The rest match the server's own limits. */
const MAX_ORGANIZATION = 120;
const MAX_MESSAGE = 1800;

const channels = [
  {
    icon: Rocket,
    title: "Book a live demo",
    description: "See PEOPLE AI Workforce in a 30-minute walkthrough tailored to your communities.",
  },
  {
    icon: Clock,
    title: "We reply quickly",
    description: "Send a note and we'll get back to you, usually within one business day.",
  },
  {
    icon: ShieldCheck,
    title: "Security questions welcome",
    description:
      "Evaluating us for PHI? Ask anything about access control, isolation and audit trails.",
  },
];

const CONTACT_PAGE_SCHEMA = {
  "@type": "ContactPage",
  name: "Contact PEOPLE AI Workforce",
  url: `${PEOPLE_ORIGIN}/contact`,
  description:
    "Get in touch with the PEOPLE AI Workforce team, or book a demo. We're happy to answer questions about features, security and pricing.",
  publisher: PUBLISHER,
  isPartOf: { "@id": WEBSITE_ID },
};

function MarketingContact() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [organization, setOrganization] = useState("");
  const [message, setMessage] = useState("");
  const [__hp, setHp] = useState(""); // honeypot (intentionally non-human field name)
  const [source] = useState(() => readAttribution("contact"));
  const [sent, setSent] = useState(false);

  /* Posts to the real endpoint. A mailto: handoff lost operators with no IT person and recorded
   * nothing, as /demo and /inquire already avoided. */
  const mutation = useMutation({
    mutationFn: async () => {
      await orpcPublicClient.crm.websiteIntake.inquiry({
        firstName,
        lastName,
        email,
        phone,
        /* The endpoint has no organization column, and losing which community is
           asking would make most of these questions unanswerable, so it rides in
           the message where whoever replies will actually read it. */
        message: organization ? `[${organization}]\n\n${message}` : message,
        relationship: "operator",
        source,
        __hp,
      });
    },
    onSuccess: () => setSent(true),
    onError: (err: Error) => {
      toast.error("We couldn't send your message", {
        description: errorWords(err, "Please try again in a moment."),
      });
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <MarketingShell>
      <IntakeHero
        id="contact"
        badge="We'd love to hear from you"
        title={
          <>
            Let's talk about your <span className="text-gradient-warm">community</span>
          </>
        }
        lead="Questions about features, security or pricing? Send us a note, or book a demo and we'll walk you through it."
      >
        <div className="mx-auto mt-14 grid max-w-5xl items-start gap-8 md:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <IntakeReasons items={channels}>
              <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/50 p-5 text-sm">
                <Mail className="size-4 text-primary" />
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="inline-flex min-h-6 items-center text-foreground underline-offset-4 hover:underline"
                  data-testid="link-email"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
            </IntakeReasons>
          </Reveal>

          {/* Form */}
          <Reveal delay={80}>
            <Card className="p-6 md:p-8">
              {sent ? (
                <div
                  className="flex flex-col items-center py-8 text-center"
                  data-testid="contact-success"
                >
                  <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                    <CheckCircle2 className="size-7" />
                  </span>
                  <h2 className="font-display mt-5 text-2xl font-semibold tracking-tight">
                    Thank you, we've got your message
                  </h2>
                  <p className="mt-3 max-w-sm text-sm text-muted-foreground">
                    Your question is with the team, carrying the community you typed, and one of us
                    will write back, usually within one business day. You can also reach us any time
                    at{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary">
                      {CONTACT_EMAIL}
                    </a>
                    .
                  </p>
                  <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <Button className="font-ui" data-testid="button-contact-demo" asChild>
                      <MLink href="/demo">
                        Book a demo
                        <ArrowRight className="ml-2 size-4" />
                      </MLink>
                    </Button>
                    {/* Clears the message rather than restoring it: the send
                          succeeded, and handing back a filled form invites the
                          same question a second time as a second lead. */}
                    <Button
                      variant="outline"
                      className="font-ui"
                      onClick={() => {
                        setMessage("");
                        setSent(false);
                      }}
                      data-testid="button-contact-reset"
                    >
                      Ask something else
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <HoneypotField id="contact-hp" value={__hp} onChange={setHp} />
                  <IntakeNameFields
                    idPrefix="contact"
                    firstName={firstName}
                    lastName={lastName}
                    onFirstName={setFirstName}
                    onLastName={setLastName}
                    placeholders={{ first: "e.g. Jane", last: "e.g. Doe" }}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="contact-org">Organization</Label>
                    <Input
                      id="contact-org"
                      maxLength={MAX_ORGANIZATION}
                      value={organization}
                      onChange={(e) => setOrganization(e.target.value)}
                      placeholder="e.g. Sunrise Senior Living"
                      data-testid="input-organization"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Work email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      maxLength={200}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. jane@yourcommunity.com"
                      data-testid="input-email"
                    />
                  </div>
                  <IntakePhoneField id="contact-phone" value={phone} onChange={setPhone} />
                  <div className="space-y-2">
                    <Label htmlFor="contact-message">How can we help?</Label>
                    <Textarea
                      id="contact-message"
                      required
                      rows={5}
                      maxLength={MAX_MESSAGE}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us about your communities and what you're looking for…"
                      data-testid="input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full font-ui"
                    disabled={mutation.isPending}
                    data-testid="button-send"
                  >
                    {mutation.isPending ? "Sending…" : "Send message"}
                    {!mutation.isPending && <ArrowRight className="ml-2 size-4" />}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Goes straight to the team. Ready to see it live?{" "}
                    <MLink href="/demo">
                      <span className="cursor-pointer text-primary" data-testid="link-demo-inline">
                        Book a demo
                      </span>
                    </MLink>
                    .
                  </p>
                </form>
              )}
            </Card>
          </Reveal>
        </div>
      </IntakeHero>
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/contact")({
  component: MarketingContact,
  head: () => ({
    ...marketingHead({
      title: "Contact: let's talk about your community | PEOPLE AI Workforce",
      description:
        "Questions about features, security or pricing? Send the team a note and hear back, usually within one business day. Security questions welcome: access control, per-organization isolation and audit trails.",
      path: "/v2/contact",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
      schema: [CONTACT_PAGE_SCHEMA, breadcrumbs({ name: "Contact", path: "/contact" })],
    }),
  }),
});
